import React from "react";
import { RiCoupon2Line } from "react-icons/ri";
import { FiTag, FiExternalLink, FiGift, FiCheckCircle, FiAward } from "react-icons/fi";
import { CopyButton } from "./CopyButton";

export const OrderCoupon = ({ orderInfo }) => {
  const isReserve = Boolean(orderInfo?.isReserveOrder);
  const couponId =
    orderInfo?.reserveCoupon?.id ||
    orderInfo?.reserveCoupon ||
    orderInfo?.coupon?.id;
  const couponObj = orderInfo?.coupon?.coupon || orderInfo?.coupon;
  const couponName = isReserve
    ? "คูปองจองสิทธิ์"
    : couponObj?.name || (couponId ? "คูปองสิทธิพิเศษ" : "");
  const couponType = couponObj?.type;
  const isVoucher = couponType === "voucher" || couponType === "voucher-lock";
  const voucherQty = isVoucher
    ? Number(couponObj?.quantity) || Number(orderInfo?.size) || 1
    : 0;

  // ตรวจจับกรณีสลากจากภารกิจ (Mission Reward)
  const isMission = Boolean(
    orderInfo?.isReward ||
      orderInfo?.missionOrder ||
      orderInfo?.missionRef ||
      (Array.isArray(orderInfo?.notes) &&
        orderInfo.notes.some(
          (n) => n?.note && String(n.note).includes("ภารกิจ")
        ))
  );
  const missionOrderId =
    orderInfo?.missionOrder ||
    orderInfo?.missionRef ||
    (Array.isArray(orderInfo?.notes) &&
      orderInfo.notes.find((n) => n?.note && String(n.note).includes("ภารกิจ"))
        ?.note) ||
    "";

  // โค้ดส่วนลด / โปรโมชั่น
  const hasPromoCode = Boolean(orderInfo?.isUsePromoCode || orderInfo?.promoCode);
  const promoCode = orderInfo?.promoCode;
  const promoDiscount = Number(orderInfo?.promoDiscount || 0);

  // คำนวณราคาและส่วนลดจริงของออเดอร์
  const rawPriceBefore = Number(orderInfo?.totalPriceBeforeDiscount || 0);
  const finalPrice = Number(orderInfo?.totalPrice || 0);

  // คำนวณส่วนลดจริงที่ได้รับ
  let calculatedDiscount = 0;
  if (rawPriceBefore > 0 && rawPriceBefore >= finalPrice) {
    calculatedDiscount = rawPriceBefore - finalPrice;
  } else if (orderInfo?.couponDiscount != null && Number(orderInfo.couponDiscount) > 0) {
    calculatedDiscount = Number(orderInfo.couponDiscount);
  } else if (orderInfo?.discount != null && Number(orderInfo.discount) > 0) {
    calculatedDiscount = Number(orderInfo.discount);
  } else if (promoDiscount > 0) {
    calculatedDiscount = promoDiscount;
  } else if (couponType === "discount" && couponObj?.discount) {
    calculatedDiscount = Number(couponObj.discount);
  } else if (isVoucher && voucherQty > 0) {
    const ticketPrice =
      orderInfo?.size > 0 && rawPriceBefore > 0
        ? rawPriceBefore / orderInfo.size
        : 100;
    calculatedDiscount = voucherQty * ticketPrice;
  } else if (isMission && orderInfo?.size > 0) {
    calculatedDiscount = (orderInfo.size || 1) * 100;
  }

  const priceBeforeDiscount =
    rawPriceBefore > 0
      ? rawPriceBefore
      : calculatedDiscount > 0
      ? finalPrice + calculatedDiscount
      : finalPrice;

  // System notes related to promo/campaign/mission
  const promoNotes = Array.isArray(orderInfo?.notes)
    ? orderInfo.notes.filter(
        (n) =>
          n?.note &&
          (n.note.includes("โปรโมชั่น") ||
            n.note.includes("คูปอง") ||
            n.note.includes("โค้ด") ||
            n.note.includes("กิจกรรม") ||
            n.note.includes("ภารกิจ"))
      )
    : [];

  const hasAnyBenefit = Boolean(
    isReserve ||
      couponId ||
      couponName ||
      hasPromoCode ||
      isMission ||
      calculatedDiscount > 0 ||
      isVoucher ||
      promoNotes.length > 0
  );

  if (!hasAnyBenefit) return null;

  return (
    <div className="bg-gradient-to-r from-emerald-50/90 via-teal-50/40 to-white border-2 border-emerald-400/80 rounded-md p-4 mb-4 shadow-xs transition-all">
      {/* แถบหัวข้อเด่น */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-3 border-b border-emerald-200/80">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-600 text-white flex items-center justify-center text-xl shadow-xs shrink-0">
            {isMission ? <FiAward /> : hasPromoCode && !couponId ? <FiTag /> : <RiCoupon2Line />}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-sm text-emerald-950">
                {isMission
                  ? "คำสั่งซื้อนี้เป็นสลากรางวัลจากภารกิจ"
                  : hasPromoCode && !couponId
                  ? "คำสั่งซื้อนี้ใช้โค้ดส่วนลดโปรโมชั่น"
                  : isReserve
                  ? "คำสั่งซื้อนี้ใช้คูปองจองสิทธิ์"
                  : "คำสั่งซื้อนี้มีสิทธิพิเศษ / ใช้คูปองส่วนลด"}
              </h4>
              <span className="px-2 py-0.5 text-[11px] font-bold rounded-md bg-emerald-600 text-white tracking-wide uppercase">
                {isMission
                  ? "MISSION REWARD"
                  : isReserve
                  ? "RESERVE COUPON"
                  : isVoucher
                  ? "VOUCHER FREE"
                  : hasPromoCode
                  ? "PROMO APPLIED"
                  : "COUPON APPLIED"}
              </span>
            </div>
            <p className="text-xs text-emerald-800 font-medium mt-0.5">
              ระบบตรวจสอบและคำนวณสิทธิประโยชน์ให้เรียบร้อยแล้ว
            </p>
          </div>
        </div>

        {/* ป้ายแสดงสิทธิประโยชน์ (ตัดคำว่า 'ประหยัด ... บาท' ออกตามสั่ง) */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {isMission ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-300 rounded-md shadow-2xs">
              <FiAward className="text-blue-600 text-sm" />
              <span className="font-bold text-xs text-blue-800">
                สลากรางวัลภารกิจ ({orderInfo?.size || 1} ใบ)
              </span>
            </div>
          ) : isVoucher ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-300 rounded-md shadow-2xs">
              <FiGift className="text-emerald-600 text-sm" />
              <span className="font-bold text-xs text-emerald-800">
                ฟรีสลาก {voucherQty} ใบ
              </span>
            </div>
          ) : isReserve ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-purple-300 rounded-md shadow-2xs">
              <span className="font-bold text-xs text-purple-700">
                📌 ใช้สิทธิ์จองสลาก
              </span>
            </div>
          ) : calculatedDiscount > 0 ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-300 rounded-md shadow-2xs">
              <span className="text-xs font-semibold text-slate-600">
                ได้รับส่วนลด:
              </span>
              <span className="font-bold text-sm text-emerald-600">
                -{calculatedDiscount.toLocaleString()} บาท
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* กล่องรายละเอียดคูปอง / โปรโมชั่น / ภารกิจ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-3">
        {/* กรณีภารกิจ (Mission Reward) */}
        {isMission && (
          <div className="bg-white border border-emerald-200/90 rounded-md p-3 flex flex-col justify-between gap-2 shadow-2xs">
            <div>
              <div className="flex items-center justify-between gap-1.5 mb-1">
                <span className="text-xs font-bold text-slate-500 flex items-center gap-1">
                  <FiAward className="text-blue-600" /> รางวัลภารกิจ
                </span>
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-md bg-blue-100 text-blue-800">
                  Mission Reward
                </span>
              </div>
              <p className="font-bold text-sm text-slate-800 break-words">
                ได้รับสลากรางวัลจากการพิชิตภารกิจ
              </p>
            </div>

            {missionOrderId && (
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[11px] text-slate-400 font-medium shrink-0">
                    รหัสภารกิจ:
                  </span>
                  <span className="text-xs font-bold text-slate-700 font-mono truncate">
                    {String(missionOrderId).slice(-10)}
                  </span>
                  <CopyButton text={missionOrderId} title="คัดลอกรหัสภารกิจ" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* รายละเอียดคูปอง */}
        {(couponId || couponName || isReserve) && (
          <div className="bg-white border border-emerald-200/90 rounded-md p-3 flex flex-col justify-between gap-2 shadow-2xs">
            <div>
              <div className="flex items-center justify-between gap-1.5 mb-1">
                <span className="text-xs font-bold text-slate-500 flex items-center gap-1">
                  <RiCoupon2Line className="text-emerald-600" /> ข้อมูลคูปอง
                </span>
                {isReserve ? (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-md bg-purple-100 text-purple-700">
                    จองสิทธิ์
                  </span>
                ) : isVoucher ? (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-md bg-amber-100 text-amber-700">
                    Voucher ฟรี
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-md bg-emerald-100 text-emerald-700">
                    ส่วนลด
                  </span>
                )}
              </div>
              <p className="font-bold text-sm text-slate-800 break-words">
                {couponName || "คูปองสิทธิพิเศษ"}
              </p>
            </div>

            {couponId && (
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[11px] text-slate-400 font-medium shrink-0">
                    รหัส:
                  </span>
                  <span className="text-xs font-bold text-slate-700 font-mono truncate">
                    {couponId}
                  </span>
                  <CopyButton text={couponId} title="คัดลอกรหัสคูปอง" />
                </div>
                <button
                  type="button"
                  onClick={() => window.open(`/coupon/${couponId}`, "_blank")}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-white bg-blue hover:opacity-90 rounded-md transition-all shrink-0 cursor-pointer shadow-2xs"
                >
                  <span>ดูคูปอง</span>
                  <FiExternalLink className="text-[11px]" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* รายละเอียดโค้ดโปรโมชั่น / กิจกรรม */}
        {hasPromoCode && (
          <div className="bg-white border border-emerald-200/90 rounded-md p-3 flex flex-col justify-between gap-2 shadow-2xs">
            <div>
              <div className="flex items-center justify-between gap-1.5 mb-1">
                <span className="text-xs font-bold text-slate-500 flex items-center gap-1">
                  <FiTag className="text-amber-500" /> โค้ดโปรโมชั่น / กิจกรรม
                </span>
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-md bg-amber-100 text-amber-800">
                  Promo Code
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono font-black text-base text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md tracking-wider">
                  {promoCode || "-"}
                </span>
                {promoCode && (
                  <CopyButton text={promoCode} title="คัดลอกโค้ดโปรโมชั่น" />
                )}
              </div>
            </div>

            {promoDiscount > 0 && (
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-semibold">
                <span className="text-slate-500">ส่วนลดจากโค้ด:</span>
                <span className="font-bold text-emerald-600">
                  -{promoDiscount.toLocaleString()} บาท
                </span>
              </div>
            )}
          </div>
        )}

        {/* สรุปยอดเปรียบเทียบ */}
        {(calculatedDiscount > 0 || priceBeforeDiscount > 0) && (
          <div className="bg-white border border-emerald-200/90 rounded-md p-3 flex flex-col justify-between gap-2 shadow-2xs">
            <div>
              <span className="text-xs font-bold text-slate-500 flex items-center gap-1 mb-1">
                <FiCheckCircle className="text-emerald-600" /> สรุปยอดชำระ
              </span>
              <div className="flex items-baseline justify-between mt-1">
                <span className="text-xs text-slate-400 font-semibold">
                  ยอดปกติ:
                </span>
                <span className="line-through text-xs font-semibold text-slate-400">
                  {priceBeforeDiscount.toLocaleString()} บาท
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-emerald-700 font-semibold">
                  {isMission
                    ? "รางวัลภารกิจ:"
                    : isVoucher
                    ? `สิทธิ์ฟรี (${voucherQty} ใบ):`
                    : isReserve
                    ? "สิทธิ์คูปองจอง:"
                    : "หักส่วนลด:"}
                </span>
                <span className="text-xs font-bold text-emerald-600">
                  -{calculatedDiscount.toLocaleString()} บาท
                </span>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 flex items-baseline justify-between">
              <span className="text-xs font-bold text-slate-800">
                จ่ายสุทธิ:
              </span>
              <span className="font-black text-base text-emerald-600">
                {finalPrice.toLocaleString()} บาท
              </span>
            </div>
          </div>
        )}
      </div>

      {/* บันทึกกิจกรรมเพิ่มเติม (ถ้ามี) */}
      {promoNotes.length > 0 && (
        <div className="mt-2.5 pt-2 border-t border-emerald-200/60 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-bold text-emerald-900">
            บันทึกกิจกรรม:
          </span>
          {promoNotes.map((n, idx) => (
            <span
              key={idx}
              className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-100/70 text-emerald-800 border border-emerald-200"
            >
              {n.note}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};


